// Runtime configuration the SPA reads at boot, from two sources in order:
//  1. window.__APP_CONFIG__ — nginx renders it from Cloud Run env vars at
//     container start, so one env-agnostic image serves every environment.
//  2. import.meta.env.VITE_* — Vite's dev env (`pnpm dev`), where
//     runtime-config.js is not served.
export interface RuntimeAppConfig {
  FIREBASE_API_KEY?: string | undefined;
  FIREBASE_AUTH_DOMAIN?: string | undefined;
  FIREBASE_PROJECT_ID?: string | undefined;
  FIREBASE_STORAGE_BUCKET?: string | undefined;
  FIREBASE_MESSAGING_SENDER_ID?: string | undefined;
  FIREBASE_APP_ID?: string | undefined;
  ALLOWED_DOMAIN?: string | undefined;
  API_BASE_URL?: string | undefined;
  USE_MOCK_API?: string | undefined;
  MARKET?: string | undefined;
  // «Notificar mismatch»: public by nature (an OAuth client id and a document id are not secrets).
  // Missing client id or spreadsheet id = the report is off in that environment.
  GOOGLE_OAUTH_CLIENT_ID?: string | undefined;
  MISMATCH_SPREADSHEET_ID?: string | undefined;
  MISMATCH_SHEET_RANGE?: string | undefined;
}

declare global {
  interface Window {
    __APP_CONFIG__?: RuntimeAppConfig;
  }
}

const VITE_KEYS: Record<keyof RuntimeAppConfig, string> = {
  FIREBASE_API_KEY: 'VITE_FIREBASE_API_KEY',
  FIREBASE_AUTH_DOMAIN: 'VITE_FIREBASE_AUTH_DOMAIN',
  FIREBASE_PROJECT_ID: 'VITE_FIREBASE_PROJECT_ID',
  FIREBASE_STORAGE_BUCKET: 'VITE_FIREBASE_STORAGE_BUCKET',
  FIREBASE_MESSAGING_SENDER_ID: 'VITE_FIREBASE_MESSAGING_SENDER_ID',
  FIREBASE_APP_ID: 'VITE_FIREBASE_APP_ID',
  ALLOWED_DOMAIN: 'VITE_ALLOWED_DOMAIN',
  API_BASE_URL: 'VITE_API_BASE_URL',
  USE_MOCK_API: 'VITE_USE_MOCK_API',
  MARKET: 'VITE_MARKET',
  GOOGLE_OAUTH_CLIENT_ID: 'VITE_GOOGLE_OAUTH_CLIENT_ID',
  MISMATCH_SPREADSHEET_ID: 'VITE_MISMATCH_SPREADSHEET_ID',
  MISMATCH_SHEET_RANGE: 'VITE_MISMATCH_SHEET_RANGE',
};

// An empty string (an envsubst placeholder that resolved to nothing) counts as absent.
export function nonEmpty(value: string | undefined): string | undefined {
  return value && value.length > 0 ? value : undefined;
}

// Per-key fallback (not all-or-nothing) so a partially-filled runtime object
// still falls back to the Vite env for whatever key it omits.
export function readRuntimeConfig(): RuntimeAppConfig {
  const runtime = (globalThis as { __APP_CONFIG__?: RuntimeAppConfig }).__APP_CONFIG__ ?? {};
  const env = import.meta.env as Record<string, string | undefined>;
  const pick = (key: keyof RuntimeAppConfig): string | undefined =>
    nonEmpty(runtime[key]) ?? nonEmpty(env[VITE_KEYS[key]]);
  return {
    FIREBASE_API_KEY: pick('FIREBASE_API_KEY'),
    FIREBASE_AUTH_DOMAIN: pick('FIREBASE_AUTH_DOMAIN'),
    FIREBASE_PROJECT_ID: pick('FIREBASE_PROJECT_ID'),
    FIREBASE_STORAGE_BUCKET: pick('FIREBASE_STORAGE_BUCKET'),
    FIREBASE_MESSAGING_SENDER_ID: pick('FIREBASE_MESSAGING_SENDER_ID'),
    FIREBASE_APP_ID: pick('FIREBASE_APP_ID'),
    ALLOWED_DOMAIN: pick('ALLOWED_DOMAIN'),
    API_BASE_URL: pick('API_BASE_URL'),
    USE_MOCK_API: pick('USE_MOCK_API'),
    MARKET: pick('MARKET'),
    GOOGLE_OAUTH_CLIENT_ID: pick('GOOGLE_OAUTH_CLIENT_ID'),
    MISMATCH_SPREADSHEET_ID: pick('MISMATCH_SPREADSHEET_ID'),
    MISMATCH_SHEET_RANGE: pick('MISMATCH_SHEET_RANGE'),
  };
}
